import React from "react";
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  ArrowUp,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";

const Footer: React.FC = () => {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const productLinks = [
    { key: "nav_features", href: "#features" },
    { key: "nav_pricing", href: "#pricing" },
    { key: "footer_link_integration", href: "#" },
    { key: "footer_link_documentation", href: "#" },
    { key: "footer_link_updates", href: "#" },
  ];

  const companyLinks = [
    { key: "nav_about", href: "#about" },
    { key: "footer_link_careers", href: "#" },
    { key: "footer_link_blog", href: "#" },
    { key: "footer_link_partners", href: "#" },
    { key: "nav_contact", href: "#contact" },
  ];

  const supportLinks = [
    { key: "footer_link_helpCenter", href: "#" },
    { key: "footer_link_terms", href: "#" },
    { key: "footer_link_privacy", href: "#" },
    { key: "footer_link_status", href: "#" },
    { key: "footer_link_contactSupport", href: "#" },
  ];

  return (
    <footer className="bg-gradient-to-br from-primary to-primary-dark text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white blur-3xl"></div>
      </div>

      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
        <button
          onClick={scrollToTop}
          className="flex items-center justify-center h-10 w-10 rounded-full bg-white text-primary shadow-lg hover:bg-gray-100 transition-colors duration-300"
          aria-label={t("footer_back_to_top_aria")}
        >
          <ArrowUp className="h-5 w-5" aria-hidden="true" />{" "}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold mb-6">{t("nav_brand")}</h3>
            <p className="text-white/80 mb-6 max-w-md">
              {t("footer_brand_description")}
            </p>
            <div className="flex space-x-4">
              {[
                { Icon: Facebook, href: "#", name: "Facebook" },
                { Icon: Twitter, href: "#", name: "Twitter" },
                { Icon: Linkedin, href: "#", name: "LinkedIn" },
                { Icon: Instagram, href: "#", name: "Instagram" },
              ].map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className="flex items-center justify-center h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300"
                  aria-label={`${t("footer_follow_us_aria")} ${item.name}`}
                >
                  <item.Icon className="h-5 w-5" aria-hidden="true" />{" "}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-6">
              {t("footer_title_product")}
            </h4>{" "}
            <ul className="space-y-4">
              {productLinks.map((item) => (
                <li key={item.key}>
                  {" "}
                  <a
                    href={item.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {t(item.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-6">
              {t("footer_title_company")}
            </h4>{" "}
            <ul className="space-y-4">
              {companyLinks.map((item) => (
                <li key={item.key}>
                  {" "}
                  <a
                    href={item.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {t(item.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-6">
              {t("footer_title_support")}
            </h4>{" "}
            <ul className="space-y-4">
              {supportLinks.map((item) => (
                <li key={item.key}>
                  {" "}
                  <a
                    href={item.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {t(item.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent my-12"></div>

        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          {" "}
          <p className="text-white/70 mb-4 md:mb-0">
            {" "}
            © {new Date().getFullYear()} {t("nav_brand")}.{" "}
            {t("footer_copyright_allRightsReserved")}
          </p>
          <div className="flex items-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 text-sm">
              <Mail className="h-4 w-4 mr-2" aria-hidden="true" />{" "}
              <a
                href={`mailto:${t("contact_info_email_address")}`}
                className="hover:underline"
              >
                {t("contact_info_email_address")}{" "}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-primary to-transparent"></div>
    </footer>
  );
};

export default Footer;
